/* eslint-disable no-empty */
import { Button, Checkbox, Form, Input, Slider } from 'antd';
import { SliderMarks } from 'antd/es/slider';
import { MasterUseCaseInterface } from 'application/usecases/MasterUseCase';
import ChevronDownIcon from 'assets/icons/chevron-down-2.svg';
import SearchIcon from 'assets/icons/search-drawer.svg';
import { MasterCompanies } from 'domain/entities/MasterEntity';
import { openErrorNoti } from 'presentation/components/Notification';
import { PageTitle } from 'presentation/components/PageTitle';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { useAppSelector } from 'presentation/redux/hooks';
import { PRIVATE_ROUTES } from 'presentation/router/CONSTANTS';
import { useEffect, useState } from 'react';
import { useCollapse } from 'react-collapsed';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AREA_CODE, AREA_ID, TYPE_FILTER, TYPE_FILTER_PARAM } from '../CU-049-01/constant';
import { DividedColumn } from '../CU-055-04/components/DividedColumn';
import { generateArrayToObject } from '../CU-055-04/helper';
import { MasterAreaState } from '../CU-055-04/models/Master';
import { convertToMasterAreaState } from './helper';
import { Searchparams } from './models/Master';

const MARK: SliderMarks = {
    0: 0,
    10: 10,
    20: 20,
    30: 30,
    40: 40,
};

interface props {
    masterUseCase: MasterUseCaseInterface;
}

const CU05505Page = ({ masterUseCase }: props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [searchParams] = useSearchParams();
    const areaCodeParam = searchParams.get(AREA_CODE) || '';
    const [masterAreasData, setMasterAreasData] = useState<MasterAreaState[]>([]);
    const [objectValueAll, setObjectValueAll] = useState<string[]>([]);
    const [isExpanded, setExpanded] = useState(true);

    const [searchText, setSearchText] = useState<string>('');
    const [searchCompany, setSearchCompany] = useState<string>('');

    const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });
    const areaIdParam = searchParams.get(AREA_ID) || '';
    const isChecking = useAppSelector((state) => state.CU05504Slice.isChecking);

    useEffect(() => {
        onFormValuesChange();
    }, [isChecking]);

    useEffect(() => {
        if (AREA_CODE)
            getAllDataMasterAreasFitList({
                areaId: Number(areaCodeParam),
            });
    }, [AREA_CODE]);

    useEffect(() => {
        getAllDataMasterAreasFitList({
            areaId: Number(areaCodeParam),
        });
    }, [searchCompany]);

    const onFormValuesChange = () => {
        const value = form.getFieldsValue();
        let isCheckAllChecked = true;
        let isId = false;
        for (const property in value) {
            if (property.includes('id')) {
                isId = true;
                if (!form.getFieldValue(property)) {
                    isCheckAllChecked = false;
                    break;
                }
            }
        }
        form.setFieldValue('all', isId && isCheckAllChecked);
    };

    const getAllDataMasterAreasFitList = async (params: { areaId: number }) => {
        try {
            dispatch(startFetching());
            const companiesData: MasterCompanies[] = await masterUseCase.getMasterCompaniesList({
                ...params,
                keyword: searchCompany,
                sortBy: 'company_cd',
            });
            if (companiesData) {
                const { objectFormAll, resultData } = convertToMasterAreaState(companiesData);
                setObjectValueAll(objectFormAll);
                setMasterAreasData(resultData);
            }
        } catch (error) {
            console;
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const getMasterLinesList = async (parentItem: MasterAreaState) => {
        try {
            dispatch(startFetching());
            const params = {
                areaId: Number(areaCodeParam),
                keyword: searchCompany,
                id: parentItem.id,
            };
            const masterLines = await masterUseCase.getMasterLinesList({
                ...params,
                sortBy: 'line_cd',
            });
            const parent = masterAreasData.find((element) => element.id === params.id);
            const objectFormAll = [...objectValueAll];
            if (parent) {
                const childCodes: any[] = [];
                const children = masterLines?.map((element) => {
                    const code = `${parent.code}.${element.lineCd}`;
                    childCodes.push(code);
                    return {
                        ...element,
                        id: element.lineCd,
                        name: element?.lineName ?? '',
                        code: code,
                        level: (parent.level ?? 0) + 1,
                        wasRequestd: false,
                        parentCode: parent?.code,
                    };
                });
                parent.children = children;
                parent.wasRequestd = true;
                masterAreasData[masterAreasData.findIndex((element) => element.id === params.id)] =
                    parent;
                setMasterAreasData([...masterAreasData]);
                setObjectValueAll([...objectFormAll, ...childCodes]);
                const isChecked = form.getFieldValue(`id${parentItem.code}`);
                form.setFieldsValue(generateArrayToObject(childCodes, isChecked));
            }
        } catch (error) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const getMasterStationList = async (parentItem: MasterAreaState) => {
        try {
            dispatch(startFetching());
            const params = {
                areaId: Number(areaCodeParam),
                keyword: searchCompany,
                id: parentItem.id,
            };
            const stations = await masterUseCase.getMasterStationsList(params);

            if (parentItem.parentCode) {
                const companyIndex = masterAreasData.findIndex(
                    (element) => element.code === parentItem.parentCode,
                );
                const company = masterAreasData[companyIndex];
                const companyChildren = company?.children ?? [];
                const parentIndex = companyChildren.findIndex(
                    (element) => element.code === parentItem.code,
                );
                if (parentIndex === -1) return;
                const parent = companyChildren[parentIndex];
                const objectFormAll = [...objectValueAll];
                if (company && company.children && parent) {
                    const childCodes: any[] = [];
                    const children = stations?.map((element) => {
                        const code = `${parent?.code}.${element.id}`;
                        childCodes.push(code);
                        return {
                            ...element,
                            id: element.id,
                            name: element?.stationName ?? '',
                            code: code,
                            level: (parent?.level ?? 0) + 1,
                            parentCode: parent?.code,
                            wasRequestd: true,
                        };
                    });
                    parent.children = children;
                    parent.wasRequestd = true;
                    company.children[parentIndex] = parent;
                    masterAreasData[companyIndex] = company;
                    setMasterAreasData([...masterAreasData]);
                    setObjectValueAll([...objectFormAll, ...childCodes]);
                    const isChecked = form.getFieldValue(`id${parentItem.code}`);
                    form.setFieldsValue(generateArrayToObject(childCodes, isChecked));
                }
            }
            return stations ?? [];
        } catch (error) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const onIsCheckAllChange = (e?: any): void => {
        const isChecked = e.target.checked;
        form.setFieldsValue(generateArrayToObject(objectValueAll, isChecked));
    };

    const submit = (value: any) => {
        let oldMin = 0;
        let oldMax = value.age ?? 40;
        if (value.age instanceof Array) {
            oldMin = value.age[0];
            oldMax = value.age[1];
        }
        const data: Searchparams = {
            areaId: Number(areaIdParam),
            areaCode: Number(areaCodeParam),
            oldMin: oldMin,
            oldMax: oldMax,
            [TYPE_FILTER_PARAM]: TYPE_FILTER.fromCU05505,
            companyCds: [],
            lineCds: [],
            stationCds: [],
            searchCompany: searchCompany,
        };
        if (!value.all) {
            Object.keys(value)
                .sort()
                .filter((element) => {
                    return element.includes('id') && value[element];
                })
                .forEach((element) => {
                    const item = element.split('.');
                    const level = item.length - 1;
                    const val = item[level];
                    if (level === 1) {
                        data.companyCds = [...(data.companyCds ?? []), val] ?? [];
                    } else if (level === 2) {
                        const companyCds = data.companyCds ?? [];
                        if ((companyCds && !companyCds.includes(item[level - 1])) || !companyCds) {
                            data.lineCds = [...(data.lineCds ?? []), val] ?? [];
                        }
                    } else if (level === 3) {
                        const companyCds = data.companyCds ?? [];
                        if ((companyCds && !companyCds.includes(item[level - 2])) || !companyCds) {
                            const lineCds = data.lineCds ?? [];
                            if ((lineCds && !lineCds.includes(item[level - 1])) || !lineCds) {
                                data.stationCds = [...(data.stationCds ?? []), val] ?? [];
                            }
                        }
                    }
                });
            // if(data.companyCds?.length === 0 && data.lineCds?.length === 0 && data.stationCds?.length === 0){
            //     data.areaId = -1;
            //     data.areaCode = -1;
            // }
        }
        navigate(`${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.CU_049_01}`, {
            state: {
                dataSearch: data,
            },
        });
    };

    const onSearchChange = (e: any) => {
        const value = e.target.value;
        setSearchText(value);
    };

    const getListChild = async (item: MasterAreaState) => {
        if (item) {
            if (item.level === 0) {
                await getMasterLinesList(item);
            } else if (item.level === 1) {
                await getMasterStationList(item);
            }
        }
    };

    const onSearch = () => {
        setSearchCompany(searchText ?? '');
    };

    return (
        <section
            className="CU-055-04-container common-container"
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexFlow: 'column',
            }}
        >
            <PageTitle title="駅を選択" />
            <section
                className="scrollable-container"
                style={{
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <Form form={form} style={{ width: 723, margin: 0, padding: 0 }} onFinish={submit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                        }
                    }}
                >
                    <section style={{ width: '100%' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 36,
                            }}
                        >
                            <Input
                                style={{ width: 645 }}
                                size="large"
                                prefix={<img alt="" src={SearchIcon} />}
                                placeholder={'駅名から探す'}
                                value={searchText}
                                onChange={onSearchChange}
                            />
                            <Button
                                className="text-search-font"
                                type="primary"
                                style={{ width: 64, height: 40, letterSpacing: -1.5 }}
                                onClick={() => onSearch()}
                            >
                                検索
                            </Button>
                        </div>
                        <div className="title">駅を選択してください</div>
                        <section
                            style={{
                                width: '100%',
                                marginTop: 16,
                            }}
                        >
                            <div
                                {...getToggleProps({
                                    onClick: () => setExpanded((prevExpanded) => !prevExpanded),
                                })}
                                style={{ borderBottom: '1px solid rgba(207, 207, 207, 0.4)' }}
                            >
                                <section
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignContent: 'center',
                                        height: 52,
                                        alignItems: 'center',
                                    }}
                                >
                                    <div
                                        onClick={(event) => {
                                            event.stopPropagation();
                                        }}
                                    >
                                        <Form.Item
                                            style={{ margin: 0, padding: 0 }}
                                            name="all"
                                            label={false}
                                            valuePropName="checked"
                                        >
                                            <Checkbox
                                                onChange={onIsCheckAllChange}
                                                className="checkbox-font-2"
                                            >
                                                すべて
                                            </Checkbox>
                                        </Form.Item>
                                    </div>

                                    <div className={`button-style center-inside`}>
                                        <img
                                            alt=""
                                            src={ChevronDownIcon}
                                            className={`cu05504-icon ${
                                                isExpanded ? 'cu05504-icon-active' : ''
                                            }`}
                                        />
                                    </div>
                                </section>
                            </div>
                            <div {...getCollapseProps()}>
                                <section
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                    }}
                                >
                                    <div style={{ width: '50%', paddingRight: 18 }}>
                                        <DividedColumn
                                            form={form}
                                            data={masterAreasData}
                                            isOdd
                                            getListChild={getListChild}
                                            maxLevel={2}
                                        />
                                    </div>
                                    <div style={{ width: '50%', paddingLeft: 18 }}>
                                        <DividedColumn
                                            form={form}
                                            data={masterAreasData}
                                            getListChild={getListChild}
                                            maxLevel={2}
                                        />
                                    </div>
                                </section>
                            </div>
                        </section>
                    </section>
                    <section style={{ marginTop: 64 }}>
                        <div className="title">築年数を選択してください</div>
                        <div style={{ width: '100%', marginTop: 16 }}>
                            <Form.Item name="age" style={{ margin: 0, padding: 0, width: '100%' }}>
                                <Slider
                                    range
                                    min={0}
                                    max={40}
                                    marks={MARK}
                                    step={1}
                                    defaultValue={[0, 40]}
                                />
                            </Form.Item>
                        </div>
                    </section>
                    <section style={{ width: '100%', marginTop: 64 }} className="center-inside">
                        <Button
                            className="button-login-text"
                            type="primary"
                            style={{ width: 343, height: 56, letterSpacing: -1.5 }}
                            htmlType="submit"
                        >
                            検索
                        </Button>
                    </section>
                    <section style={{ height: 64 }}></section>
                </Form>
            </section>
        </section>
    );
};

export default CU05505Page;
